import React, { useRef, useState, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import { X, Camera, RotateCcw, SwitchCamera, Loader2, Clock, LogOut, AlertTriangle, Smartphone, FileText, MapPinOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { useAttendanceGate } from '@/context/AttendanceContext';

// Import shared components
import { CameraOverlay } from './CameraOverlay';
import { CountdownTimer } from './CountdownTimer';
import { GPSIndicator } from './GPSIndicator';
import { SuccessCelebration } from './SuccessCelebration';
import { PhotoQualityIndicator } from './PhotoQualityIndicator';
import { addGpsWatermark } from '@/utils/photoWatermark';

// Warning thresholds (matching backend constants)
const GPS_ACCURACY_WARNING_THRESHOLD = 100; // meters

// GPS timeout: 30 seconds before offering manual fallback (REBUILD-09)
const GPS_TIMEOUT_MS = 30000;

// Helper to calculate duration between check-in and now
function calculateDuration(checkInTimeStr) {
  if (!checkInTimeStr) return null;
  const checkIn = new Date(checkInTimeStr);
  const now = new Date();
  const diffMs = now - checkIn;
  if (diffMs < 0) return { hours: 0, minutes: 0 };
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return { hours, minutes };
}

// Helper: format time in IST
function formatTimeIST(dateStr) {
  if (!dateStr) return '--:--';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

export default function CheckOutModal() {
  const {
    showCheckOutModal,
    setShowCheckOutModal,
    handleCheckOutSuccess,
    checkedInLocation,
    checkInTime,
    isCheckedIn
  } = useAttendanceGate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);

  // State
  const [capturedImage, setCapturedImage] = useState(null);
  const [retakeCount, setRetakeCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facingMode, setFacingMode] = useState('user');
  const [showCountdown, setShowCountdown] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [step, setStep] = useState('info'); // 'info', 'camera', 'preview', 'submitting', 'success'
  const [duration, setDuration] = useState(null);

  // Warning states (EDGE-02)
  const [gpsWarning, setGpsWarning] = useState(null);
  const [distanceWarning, setDistanceWarning] = useState(null);

  // Photo fallback states (EDGE-03)
  const [photoError, setPhotoError] = useState(false);
  const [fallbackMode, setFallbackMode] = useState(null); // 'phone', 'text'
  const [fallbackReason, setFallbackReason] = useState('');
  const [uploadedPhoto, setUploadedPhoto] = useState(null);

  // GPS timeout and manual fallback states (REBUILD-09)
  const [gpsTimedOut, setGpsTimedOut] = useState(false);
  const [gpsTimeRemaining, setGpsTimeRemaining] = useState(30);
  const [gpsFallback, setGpsFallback] = useState(false);
  const gpsTimerRef = useRef(null);
  const gpsCountdownRef = useRef(null);

  const MAX_RETAKES = 3;

  // Calculate duration on mount and update every minute
  useEffect(() => {
    if (checkInTime) {
      setDuration(calculateDuration(checkInTime));

      const interval = setInterval(() => {
        setDuration(calculateDuration(checkInTime));
      }, 60000); // Update every minute

      return () => clearInterval(interval);
    }
  }, [checkInTime]);

  // Video constraints
  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1280 },
    height: { ideal: 720 }
  };

  // Toggle camera
  const toggleCamera = useCallback(() => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  // Start countdown
  const startCapture = () => {
    setShowCountdown(true);
  };

  // Countdown complete
  const handleCountdownComplete = async () => {
    setShowCountdown(false);
    if (webcamRef.current) {
      const rawImage = webcamRef.current.getScreenshot();
      if (rawImage) {
        try {
          const watermarked = await addGpsWatermark(rawImage, {
            locationName: checkedInLocation || 'Unknown',
            locationType: 'gym',
            latitude: gpsLocation?.latitude || null,
            longitude: gpsLocation?.longitude || null,
            accuracy: gpsLocation?.accuracy || null,
            type: 'check-out',
          });
          setCapturedImage(watermarked);
        } catch {
          setCapturedImage(rawImage);
        }
        setStep('preview');
      } else {
        toast.error('Failed to capture photo. Please try again.');
      }
    }
  };

  // Retake photo
  const retake = () => {
    if (retakeCount >= MAX_RETAKES) {
      toast.error('Maximum retakes reached.');
      return;
    }
    setCapturedImage(null);
    setRetakeCount(prev => prev + 1);
    setStep('camera');
  };

  // GPS location update handler with accuracy warning check (EDGE-02)
  const handleLocationUpdate = useCallback((location) => {
    setGpsLocation(location);

    // GPS acquired — clear timeout timers (REBUILD-09)
    clearTimeout(gpsTimerRef.current);
    clearInterval(gpsCountdownRef.current);
    setGpsTimedOut(false);

    // Check accuracy warning (>100m)
    if (location.accuracy > GPS_ACCURACY_WARNING_THRESHOLD) {
      setGpsWarning({
        type: 'poor_accuracy',
        message: `GPS accuracy is ${Math.round(location.accuracy)}m. Check-out will be flagged for review.`
      });
    } else {
      setGpsWarning(null);
    }
  }, []);

  // Start GPS timeout when modal opens and GPS is not yet acquired (REBUILD-09)
  useEffect(() => {
    if (!showCheckOutModal || gpsLocation || gpsFallback) return;

    // Start 30-second timeout
    setGpsTimeRemaining(30);
    setGpsTimedOut(false);

    gpsTimerRef.current = setTimeout(() => {
      setGpsTimedOut(true);
    }, GPS_TIMEOUT_MS);

    gpsCountdownRef.current = setInterval(() => {
      setGpsTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(gpsCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(gpsTimerRef.current);
      clearInterval(gpsCountdownRef.current);
    };
  }, [showCheckOutModal, gpsLocation, gpsFallback]);

  // Handle manual GPS fallback — user confirms their location manually (REBUILD-09)
  const handleGpsFallback = () => {
    setGpsFallback(true);
    setGpsTimedOut(false);
    clearTimeout(gpsTimerRef.current);
    clearInterval(gpsCountdownRef.current);
    // Proceed to camera step without GPS coordinates
    setStep('camera');
  };

  // Retry GPS acquisition (REBUILD-09)
  const retryGps = () => {
    setGpsTimedOut(false);
    setGpsTimeRemaining(30);
    setGpsFallback(false);
    setGpsLocation(null);
    // Re-start timeout timer
    gpsTimerRef.current = setTimeout(() => {
      setGpsTimedOut(true);
    }, GPS_TIMEOUT_MS);
    gpsCountdownRef.current = setInterval(() => {
      setGpsTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(gpsCountdownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle webcam error - show fallback options (EDGE-03)
  const handleWebcamError = useCallback((error) => {
    console.error('Webcam error:', error);
    setPhotoError(true);
    setFallbackMode('phone');
  }, []);

  // Handle file upload from phone camera fallback
  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedPhoto(e.target.result);
        setCapturedImage(e.target.result);
        setFallbackMode(null);
        setStep('preview');
      };
      reader.readAsDataURL(file);
    }
  }, []);

  // Proceed to camera
  const proceedToCamera = () => {
    if (!gpsLocation && !gpsFallback) {
      toast.error('Please wait for GPS location');
      return;
    }
    setStep('camera');
  };

  // Submit check-out with fallback support
  const handleSubmit = async () => {
    // Check for either captured image or fallback reason
    if (!capturedImage && !fallbackReason) {
      toast.error('Please take a photo or provide a reason for no photo');
      return;
    }

    if (!gpsLocation && !gpsFallback) {
      toast.error('GPS location required.');
      return;
    }

    setLoading(true);
    setStep('submitting');

    try {
      const formData = new FormData();

      // Only include GPS coordinates when not using manual fallback (REBUILD-09)
      if (gpsLocation && !gpsFallback) {
        formData.append('latitude', gpsLocation.latitude);
        formData.append('longitude', gpsLocation.longitude);
        formData.append('accuracy', gpsLocation.accuracy);
      }

      // Include gps_fallback flag when manual fallback was used (REBUILD-09)
      if (gpsFallback) {
        formData.append('gps_fallback', 'true');
      }

      // Add photo or fallback reason
      if (capturedImage) {
        const blob = await fetch(capturedImage).then(r => r.blob());
        formData.append('photo', blob, 'checkout.jpg');
      } else if (fallbackReason) {
        // Text fallback: no photo, just reason
        formData.append('photo_fallback_reason', fallbackReason);
      }

      const response = await axiosInstance.post('/attendance/daily/check-out', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Show warning if flagged
      if (response.data?.flagged_for_review) {
        toast.warning(response.data.warning || 'Check-out flagged for review');
      }

      setShowCelebration(true);
      setStep('success');
    } catch (error) {
      // Network error (no server response)
      if (!error.response) {
        toast.error('Network error. Please check your internet connection and try again.', { duration: 15000 });
        setStep('preview');
        setLoading(false);
        return;
      }

      setStep('preview');
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        const message = typeof detail === 'object' ? detail.message : detail;
        const action = typeof detail === 'object' ? detail.action : null;
        toast.error(action ? `${message} ${action}` : message, { duration: 15000 });
      } else {
        toast.error('Check-out failed. Please try again.', { duration: 15000 });
      }
    } finally {
      setLoading(false);
    }
  };

  // Submit with text fallback reason
  const handleTextFallbackSubmit = async () => {
    if (!fallbackReason.trim()) {
      toast.error('Please provide a reason for no photo');
      return;
    }
    await handleSubmit();
  };

  // Celebration complete
  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    handleCheckOutSuccess();
  };

  // Close modal
  const handleClose = () => {
    setCapturedImage(null);
    setRetakeCount(0);
    setShowCountdown(false);
    setStep('info');
    setGpsWarning(null);
    setDistanceWarning(null);
    setPhotoError(false);
    setFallbackMode(null);
    setFallbackReason('');
    setUploadedPhoto(null);
    // Reset GPS fallback state (REBUILD-09)
    setGpsTimedOut(false);
    setGpsTimeRemaining(30);
    setGpsFallback(false);
    clearTimeout(gpsTimerRef.current);
    clearInterval(gpsCountdownRef.current);
    setShowCheckOutModal(false);
  };

  if (!showCheckOutModal || !isCheckedIn) return null;

  // Show celebration
  if (showCelebration) {
    return (
      <SuccessCelebration
        title="Checked Out!"
        subtitle={duration ? `Shift duration: ${duration.hours}h ${duration.minutes}m` : 'Great work today!'}
        onComplete={handleCelebrationComplete}
        autoDismissMs={2500}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
      <div className="relative w-full h-full md:max-w-lg md:h-auto md:max-h-[90vh] bg-white rounded-3xl overflow-hidden flex flex-col shadow-2xl">

        {/* Header — clean white */}
        <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Check Out</h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Step 1: Info — Check-in summary & duration */}
          {step === 'info' && (
            <>
              {/* Hero summary */}
              <div className="text-center py-2">
                <p className="text-2xl font-bold text-slate-800">Ready to check out?</p>
                <p className="text-sm text-slate-500 mt-1">Review your shift and take a photo</p>
              </div>

              {/* Location + check-in time card */}
              <div className="p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {checkedInLocation || 'Unknown location'}
                    </p>
                    <p className="text-sm text-slate-500">
                      Checked in at {formatTimeIST(checkInTime)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Duration — big and bold */}
              {duration && (
                <div className="text-center py-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Time Worked</p>
                  <p className="text-4xl font-bold text-slate-800 font-mono">
                    {String(duration.hours).padStart(2, '0')}:{String(duration.minutes).padStart(2, '0')}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {duration.hours >= 8 ? 'Great shift!' : duration.hours >= 6 ? 'Good progress' : 'Short shift today'}
                  </p>
                </div>
              )}

              {/* GPS — silent background */}
              <div className="hidden">
                <GPSIndicator onLocationUpdate={handleLocationUpdate} showRetryButton={false} />
              </div>

              {/* Auto-fallback GPS if timed out */}
              {gpsTimedOut && !gpsLocation && !gpsFallback && (() => { handleGpsFallback(); return null; })()}

              {/* GPS warning — only with real data + distance mismatch */}
              {gpsLocation && !gpsFallback && (gpsWarning || distanceWarning) && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <span>GPS shows you may be far from this location. Check-out will be flagged for review.</span>
                </div>
              )}

              {/* Continue button — always enabled (GPS is background) */}
              <Button
                onClick={() => {
                  if (!gpsLocation && !gpsFallback) handleGpsFallback();
                  proceedToCamera();
                }}
                className="w-full h-14 text-lg font-semibold rounded-2xl bg-[#7C1D3E] hover:bg-[#5C0E2E] text-white shadow-lg shadow-[#7C1D3E]/30 hover:shadow-[#7C1D3E]/50 transition-all"
              >
                <Camera className="w-5 h-5 mr-2" />
                Continue — Take Photo
              </Button>
            </>
          )}

          {/* Step 2: Camera */}
          {step === 'camera' && (
            <>
              {/* Normal camera view or fallback UI */}
              {!photoError ? (
                <>
                  <div className="camera-frame aspect-[3/4] bg-black relative">
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/jpeg"
                      screenshotQuality={0.9}
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover"
                      mirrored={facingMode === 'user'}
                      onUserMediaError={handleWebcamError}
                    />

                    <CameraOverlay />

                    {showCountdown && (
                      <CountdownTimer
                        duration={3}
                        onComplete={handleCountdownComplete}
                      />
                    )}

                    <button
                      onClick={toggleCamera}
                      className="absolute top-4 right-4 p-3 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-colors z-10"
                    >
                      <SwitchCamera className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Photo quality indicator */}
                  <PhotoQualityIndicator
                    webcamRef={webcamRef}
                    isActive={step === 'camera' && !showCountdown}
                    className="bg-slate-100/80 rounded-xl px-4 py-2 justify-center"
                  />

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setStep('info')}
                      className="flex-1 h-12 rounded-xl"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={startCapture}
                      disabled={showCountdown}
                      className="flex-1 h-12 rounded-xl gradient-maroon text-white"
                    >
                      <Camera className="w-5 h-5 mr-2" />
                      Take Photo
                    </Button>
                  </div>
                </>
              ) : (
                /* Photo Fallback UI (EDGE-03) */
                <div className="space-y-4">
                  {/* Phone camera fallback - first option */}
                  {fallbackMode === 'phone' && (
                    <div className="text-center p-6 border-2 border-dashed border-slate-200 rounded-xl bg-slate-50">
                      <Smartphone className="h-12 w-12 mx-auto text-blue-600 mb-3" />
                      <h3 className="font-semibold text-slate-800 mb-2">Camera not available</h3>
                      <p className="text-sm text-slate-600 mb-4">
                        Please use your phone's camera app to take a selfie, then upload it here.
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="user"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="photo-upload-checkout"
                      />
                      <label htmlFor="photo-upload-checkout">
                        <Button asChild className="gradient-maroon text-white">
                          <span>
                            <Camera className="w-4 h-4 mr-2" />
                            Take Photo with Phone
                          </span>
                        </Button>
                      </label>
                      <Button
                        variant="link"
                        onClick={() => setFallbackMode('text')}
                        className="mt-3 text-slate-500"
                      >
                        Camera still not working?
                      </Button>
                    </div>
                  )}

                  {/* Text reason fallback - last resort */}
                  {fallbackMode === 'text' && (
                    <div className="p-4 border rounded-xl bg-amber-50 border-amber-200">
                      <div className="flex items-center gap-2 text-amber-800 mb-3">
                        <FileText className="h-5 w-5" />
                        <h3 className="font-semibold">Enter reason for no photo</h3>
                      </div>
                      <textarea
                        value={fallbackReason}
                        onChange={(e) => setFallbackReason(e.target.value)}
                        placeholder="Explain why you cannot take a photo (e.g., camera broken, device issue)..."
                        className="w-full p-3 border border-amber-300 rounded-lg min-h-[100px] bg-white text-slate-800"
                      />
                      <p className="text-xs text-amber-600 mt-2">
                        This will be flagged for manager review and approval.
                      </p>
                      <div className="flex gap-3 mt-4">
                        <Button
                          variant="outline"
                          onClick={() => setFallbackMode('phone')}
                          className="flex-1"
                        >
                          Back
                        </Button>
                        <Button
                          onClick={handleTextFallbackSubmit}
                          disabled={!fallbackReason.trim() || loading}
                          className="flex-1 gradient-maroon text-white"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            'Submit Without Photo'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Back button for fallback modes */}
                  {fallbackMode && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setPhotoError(false);
                        setFallbackMode(null);
                        setStep('info');
                      }}
                      className="w-full"
                    >
                      Back to Location
                    </Button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && capturedImage && (
            <>
              {/* GPS Warning Banner in preview (EDGE-02) */}
              {(gpsWarning || distanceWarning) && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium">Review Required</span>
                  </div>
                  <p className="text-sm text-amber-700 mt-1">
                    {gpsWarning?.message || distanceWarning?.message}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Your check-out will be flagged for manager review.
                  </p>
                </div>
              )}

              <div className="camera-frame aspect-[3/4] bg-black relative">
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={retake}
                  disabled={retakeCount >= MAX_RETAKES || loading}
                  className="flex-1 h-12 rounded-xl"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Retake {retakeCount < MAX_RETAKES && `(${MAX_RETAKES - retakeCount})`}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 rounded-xl gradient-maroon text-white"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Checking out...
                    </>
                  ) : (
                    <>
                      <LogOut className="w-4 h-4 mr-2" />
                      Submit Check-Out
                    </>
                  )}
                </Button>
              </div>

              {/* GPS confirmation with warning indicator */}
              {gpsLocation && !gpsFallback && (
                <div className={`text-center text-sm ${gpsWarning ? 'text-amber-600' : 'text-slate-500'}`}>
                  Location: {gpsLocation.accuracy.toFixed(0)}m accuracy
                  {gpsWarning && ' (flagged)'}
                </div>
              )}
              {/* GPS fallback indicator (REBUILD-09) */}
              {gpsFallback && (
                <div className="text-center text-sm text-amber-600">
                  Manual location confirmation (flagged for review)
                </div>
              )}
            </>
          )}

          {/* Step 4: Submitting */}
          {step === 'submitting' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="w-12 h-12 text-maroon-500 animate-spin" />
              <p className="text-slate-600 font-medium">Processing your check-out...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
